/**
 * Catálogo de expressões para os campos avaliados como código.
 *
 * Campos de texto passam pelo Handlebars (helpersDoc), mas três campos são
 * avaliados como JavaScript pelo executor — o código do passo "Executar script
 * js", o "Valor de referência" da repetição e o valor das seleções
 * referenciadas (refUniqueSelection/refMultipleSelection). Nesses, o serviço
 * chama a VM (`vm.service.ts`) em vez do TemplateConvertService, então um
 * `{{ upcase x }}` entraria como texto literal e o campo gravaria a chave sem
 * resolver. Daí a lista separada: os auxiliares do Handlebars não valem aqui,
 * e o que vale é o JavaScript padrão mais o `moment`, que é a única biblioteca
 * injetada no sandbox.
 *
 * Tudo aqui é expressão, nunca comando: o "Valor de referência" e as seleções
 * referenciadas são embrulhados num `return <código>`, onde um `const x = ...`
 * não compila.
 */

/**
 * Marcador do lugar onde entra a anotação. Depois de inserir, o editor deixa a
 * primeira ocorrência selecionada — clicar numa anotação da lista a substitui.
 */
export const CODE_HELPER_PLACEHOLDER = "VALOR";

export const codeHelpersDoc = {
    "Data e hora": [
        {
            name: "Formatar data",
            insert: 'moment(VALOR).format("DD/MM/YYYY")',
            description: "Escreve a data no formato indicado.",
            example: '"2026-08-13T09:00:00Z" -> "13/08/2026"',
        },
        {
            name: "Formatar data e hora",
            insert: 'moment(VALOR).format("DD/MM/YYYY HH:mm")',
            description: "Mesma coisa, incluindo o horário.",
            example: '"2026-08-13T09:00:00Z" -> "13/08/2026 09:00"',
        },
        {
            name: "Data de hoje",
            insert: 'moment().format("YYYY-MM-DD")',
            description: "Momento em que a automação executa, sem depender de nenhuma anotação.",
            example: '-> "2026-08-13"',
        },
        {
            name: "Somar período",
            insert: 'moment(VALOR).add(1, "days").toISOString()',
            description: 'Avança a data. Trocar a unidade por "hours", "months" ou "years" conforme o caso.',
            example: '"2026-08-13" -> "2026-08-14T00:00:00.000Z"',
        },
        {
            name: "Subtrair período",
            insert: 'moment(VALOR).subtract(1, "months").toISOString()',
            description: "Recua a data pelo período indicado.",
            example: '"2026-08-13" -> "2026-07-13T00:00:00.000Z"',
        },
        {
            name: "Início do dia",
            insert: 'moment(VALOR).startOf("day").toISOString()',
            description: 'Zera a hora. Usar "month" para o primeiro dia do mês.',
            example: '"2026-08-13T09:00:00Z" -> "2026-08-13T00:00:00.000Z"',
        },
        {
            name: "Diferença entre datas",
            insert: 'moment(VALOR).diff(moment(OUTRA_DATA), "days")',
            description: "Quantos dias separam as duas datas. Negativo quando a primeira é anterior.",
            example: '"2026-08-13" e "2026-08-10" -> 3',
        },
        {
            name: "Comparar datas",
            insert: "moment(VALOR).isBefore(moment(OUTRA_DATA))",
            description: "Verdadeiro se a primeira data vier antes. Existe também isAfter.",
            example: '"2026-08-10" e "2026-08-13" -> true',
        },
        {
            name: "Data válida?",
            insert: "moment(VALOR).isValid()",
            description: "Falso quando o valor não é uma data reconhecível.",
            example: '"13/08/2026" -> true, "abc" -> false',
        },
    ],
    "Texto": [
        {
            name: "Converter para texto",
            insert: "String(VALOR)",
            description: "Garante texto mesmo quando a anotação traz número ou booleano.",
            example: "123 -> \"123\"",
        },
        {
            name: "Juntar textos",
            // O `${}` é o texto que vai para o editor do usuário, não uma
            // interpolação deste arquivo.
            // eslint-disable-next-line no-template-curly-in-string
            insert: "`Protocolo ${VALOR}`",
            description: "Monta um texto com anotações no meio, entre crase.",
            example: '"1234" -> "Protocolo 1234"',
        },
        {
            name: "Maiúsculas",
            insert: "String(VALOR).toUpperCase()",
            description: "Converte todo o texto para maiúsculas.",
            example: '"canto" -> "CANTO"',
        },
        {
            name: "Minúsculas",
            insert: "String(VALOR).toLowerCase()",
            description: "Converte todo o texto para minúsculas.",
            example: '"CANTO" -> "canto"',
        },
        {
            name: "Remover espaços das pontas",
            insert: "String(VALOR).trim()",
            description: "Tira espaços do começo e do fim.",
            example: '"  canto  " -> "canto"',
        },
        {
            name: "Substituir trecho",
            insert: 'String(VALOR).replace(/de/g, "para")',
            description: "Troca todas as ocorrências do primeiro trecho pelo segundo.",
            example: '"de casa de novo" -> "para casa para novo"',
        },
        {
            name: "Somente números",
            insert: 'String(VALOR).replace(/\\D/g, "")',
            description: "Descarta tudo que não for dígito — útil para CPF, CNPJ e telefone.",
            example: '"(11) 99999-0000" -> "11999990000"',
        },
        {
            name: "Recortar trecho",
            insert: "String(VALOR).slice(0, 10)",
            description: "Pega os primeiros caracteres do texto.",
            example: '"2026-08-13T09:00:00Z" -> "2026-08-13"',
        },
        {
            name: "Completar à esquerda",
            insert: 'String(VALOR).padStart(6, "0")',
            description: "Preenche até o tamanho indicado, à esquerda.",
            example: '"42" -> "000042"',
        },
        {
            name: "Dividir em lista",
            insert: 'String(VALOR).split(",")',
            description: "Quebra o texto pelo separador e devolve uma lista.",
            example: '"a,b,c" -> ["a", "b", "c"]',
        },
        {
            name: "Contém trecho?",
            insert: 'String(VALOR).includes("texto")',
            description: "Verdadeiro quando o trecho aparece no texto.",
            example: '"contrato assinado" com "assinado" -> true',
        },
    ],
    "Número": [
        {
            name: "Converter para número",
            insert: "Number(VALOR)",
            description: "Transforma o texto em número antes de calcular — sem isso, + concatena.",
            example: '"10" -> 10',
        },
        {
            name: "Somar",
            insert: "Number(VALOR) + Number(OUTRO_VALOR)",
            description: "Soma duas anotações numéricas.",
            example: '"10" e "5" -> 15',
        },
        {
            name: "Casas decimais",
            insert: "Number(VALOR).toFixed(2)",
            description: "Devolve texto com o número de casas indicado.",
            example: '10.5 -> "10.50"',
        },
        {
            name: "Arredondar",
            insert: "Math.round(Number(VALOR))",
            description: "Arredonda para o inteiro mais próximo. Math.floor e Math.ceil forçam a direção.",
            example: "10.6 -> 11",
        },
        {
            name: "Valor absoluto",
            insert: "Math.abs(Number(VALOR))",
            description: "Descarta o sinal negativo.",
            example: "-8 -> 8",
        },
        {
            name: "Maior entre dois",
            insert: "Math.max(Number(VALOR), 0)",
            description: "Devolve o maior dos valores. Math.min devolve o menor.",
            example: "-3 e 0 -> 0",
        },
    ],
    "Lista": [
        {
            name: "Quantidade de itens",
            insert: "VALOR.length",
            description: "Quantos itens a lista tem.",
            example: "[a, b, c] -> 3",
        },
        {
            name: "Primeiro item",
            insert: "VALOR[0]",
            description: "Pega o item inicial da lista.",
            example: '["a", "b"] -> "a"',
        },
        {
            name: "Extrair um campo de cada item",
            insert: "VALOR.map(item => item.campo)",
            description: "Devolve a lista só com o campo indicado de cada item.",
            example: '[{campo: "a"}, {campo: "b"}] -> ["a", "b"]',
        },
        {
            name: "Filtrar itens",
            insert: 'VALOR.filter(item => item.campo === "x")',
            description: "Mantém apenas os itens que atendem à condição.",
            example: '[{campo: "x"}, {campo: "y"}] -> [{campo: "x"}]',
        },
        {
            name: "Encontrar um item",
            insert: 'VALOR.find(item => item.campo === "x")',
            description: "Devolve o primeiro item que atende à condição, ou vazio.",
            example: '[{campo: "x"}] -> {campo: "x"}',
        },
        {
            name: "Juntar em texto",
            insert: 'VALOR.join(", ")',
            description: "Cola os itens da lista num único texto.",
            example: '["a", "b"] -> "a, b"',
        },
        {
            name: "Somar um campo",
            insert: "VALOR.reduce((total, item) => total + Number(item.campo), 0)",
            description: "Total do campo indicado em todos os itens.",
            example: "[{campo: 10}, {campo: 5}] -> 15",
        },
        {
            name: "Primeiros itens",
            insert: "VALOR.slice(0, 5)",
            description: "Recorta a lista até a quantidade indicada.",
            example: "[a, b, c, d, e, f] -> [a, b, c, d, e]",
        },
        {
            name: "Tem algum item?",
            insert: "VALOR.length > 0",
            description: "Verdadeiro quando a lista não está vazia.",
            example: "[] -> false",
        },
    ],
    "Objeto e JSON": [
        {
            name: "Campo do objeto",
            insert: "VALOR?.campo",
            description: "Lê o campo sem quebrar quando o objeto vem vazio.",
            example: '{campo: "a"} -> "a"',
        },
        {
            name: "Texto JSON do valor",
            insert: "JSON.stringify(VALOR)",
            description: "Serializa o objeto ou a lista como texto.",
            example: '{campo: "a"} -> \'{"campo":"a"}\'',
        },
        {
            name: "Ler texto JSON",
            insert: "JSON.parse(VALOR)",
            description: "Converte um texto JSON de volta em objeto.",
            example: '\'{"campo":"a"}\' -> {campo: "a"}',
        },
        {
            name: "Nomes dos campos",
            insert: "Object.keys(VALOR)",
            description: "Lista com os nomes dos campos do objeto.",
            example: '{a: 1, b: 2} -> ["a", "b"]',
        },
        {
            name: "Valores dos campos",
            insert: "Object.values(VALOR)",
            description: "Lista com os valores do objeto.",
            example: "{a: 1, b: 2} -> [1, 2]",
        },
    ],
    "Condição": [
        {
            name: "Se / senão",
            insert: 'VALOR ? "sim" : "não"',
            description: "Escolhe entre dois resultados conforme a condição.",
            example: 'true -> "sim"',
        },
        {
            name: "Valor padrão",
            insert: 'VALOR ?? "padrão"',
            description: "Usa o segundo valor quando a anotação vem vazia.",
            example: 'vazio -> "padrão"',
        },
        {
            name: "Igual a",
            insert: 'VALOR === "x"',
            description: "Compara valor e tipo, sem conversão automática.",
            example: '"x" -> true',
        },
        {
            name: "Está vazio?",
            insert: '!VALOR || String(VALOR).trim() === ""',
            description: "Verdadeiro para vazio, nulo ou só espaços.",
            example: '"   " -> true',
        },
        {
            name: "Verdadeiro / falso",
            insert: "Boolean(VALOR)",
            description: "Converte o valor em booleano.",
            example: '"" -> false',
        },
    ],
};
